import { useState, useEffect, useRef, useMemo } from "react";
import { Search, X, MapPin, AlertCircle } from "lucide-react";

/**
 * MapSearchBar — kompakt søgefunktion til kortet.
 *
 * Søger på tværs af: navn, beskrivelse, område, punkt-type og fraktioner i containers/bins.
 * Hvert resultat åbnes ved klik → onSelectResult(point) som ejer-komponenten bruger til
 * at flyve til punktet og pulse i 3 sek.
 *
 * Genveje:
 *  - Ctrl/Cmd+K eller "/" → åbn
 *  - Piletaster ↑/↓ → naviger
 *  - Enter → vælg
 *  - Esc → luk
 */
export default function MapSearchBar({ points = [], maxicontainers = [], areas = [], workZones = [], onSelectResult }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Areas-lookup map så vi hurtigt kan slå area_name op via area_id
  const areaMap = useMemo(() => {
    const m = {};
    areas.forEach(a => { if (a.id) m[a.id] = a.name || ''; });
    return m;
  }, [areas]);

  // Type-label oversættelse
  const TYPE_LABELS = {
    'ressourceø': 'Ressourceø',
    'ressourcespand': 'Ressourcespand',
    'drikkevand': 'Vandpost',
    'pausetelt': 'Pausetelt',
    'snack': 'Snack',
    'kaffe_te': 'Kaffe/Te',
    'bod': 'Bod',
    'bar': 'Bar',
    'toilet': 'Toilet',
    'pissoir': 'Pissoir',
    'opmærksomhedspunkt': 'Opmærksomhedspunkt',
    'maxicontainer': 'Maxicontainer',
  };

  // Status-farve til lille indikator
  const statusColor = (s) => s === 'rød' ? '#ef4444' : s === 'gul' ? '#eab308' : '#22c55e';

  // Saml alle søgbare items (points + maxicontainere)
  const allItems = useMemo(() => {
    const items = [];
    points.forEach(p => {
      const fractions = []
        .concat((p.containers || []).map(c => c.fraction_type || ''))
        .concat((p.bins || []).map(b => b.fraction_type || ''))
        .filter(Boolean);
      items.push({
        kind: 'point',
        id: p.id,
        name: p.name || '',
        description: p.description || '',
        type: p.point_type || '',
        typeLabel: TYPE_LABELS[p.point_type] || p.point_type || '',
        areaName: areaMap[p.area_id] || '',
        fractions,
        status: p.status,
        lat: p.lat, lng: p.lng,
        hasCoords: p.lat != null && p.lng != null,
        ref: p,
      });
    });
    maxicontainers.forEach(m => {
      items.push({
        kind: 'maxi',
        id: m.id,
        name: m.name || '',
        description: m.description || '',
        type: 'maxicontainer',
        typeLabel: 'Maxicontainer',
        areaName: areaMap[m.area_id] || '',
        fractions: [m.fraction || ''].filter(Boolean),
        status: m.status,
        lat: m.lat, lng: m.lng,
        hasCoords: m.lat != null && m.lng != null,
        ref: m,
      });
    });
    // Arbejdsområder (work_zones) — beregn centroid fra polygon-hjørner til flyTo
    workZones.forEach(wz => {
      const corners = wz.corners || [];
      let lat = null, lng = null;
      if (corners.length > 0) {
        lat = corners.reduce((s, c) => s + (c.lat || 0), 0) / corners.length;
        lng = corners.reduce((s, c) => s + (c.lng || 0), 0) / corners.length;
      }
      items.push({
        kind: 'workZone',
        id: wz.id,
        name: wz.label || wz.name || '',
        description: '',
        type: 'workzone',
        typeLabel: 'Arbejdsområde',
        areaName: areaMap[wz.area_id] || '',
        fractions: [],
        status: null,
        lat, lng,
        hasCoords: lat != null && lng != null,
        ref: { ...wz, lat, lng },
      });
    });
    return items;
  }, [points, maxicontainers, workZones, areaMap]);

  // Filtrer baseret på query (OR-match på tværs af felter)
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    const matchOne = (item, token) => {
      return (
        item.name.toLowerCase().includes(token) ||
        item.description.toLowerCase().includes(token) ||
        item.typeLabel.toLowerCase().includes(token) ||
        item.type.toLowerCase().includes(token) ||
        item.areaName.toLowerCase().includes(token) ||
        item.fractions.some(f => f.toLowerCase().includes(token))
      );
    };
    // Alle tokens skal matche mindst ét felt (AND mellem tokens, OR mellem felter)
    const filtered = allItems.filter(item => tokens.every(t => matchOne(item, t)));
    // Sortering: eksakt navn-match først, så starts-with på navn, så øvrige
    return filtered.sort((a, b) => {
      const an = a.name.toLowerCase(), bn = b.name.toLowerCase();
      const aExact = an === q ? 0 : 1;
      const bExact = bn === q ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      const aStart = an.startsWith(q) ? 0 : 1;
      const bStart = bn.startsWith(q) ? 0 : 1;
      if (aStart !== bStart) return aStart - bStart;
      return an.localeCompare(bn, 'da');
    }).slice(0, 50);
  }, [query, allItems]);

  // Global keyboard shortcut (Ctrl/Cmd+K eller /)
  useEffect(() => {
    const onKey = (e) => {
      const isModK = (e.key === 'k' || e.key === 'K') && (e.ctrlKey || e.metaKey);
      const isSlash = e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey;
      const tag = (e.target?.tagName || '').toLowerCase();
      const inField = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (isModK || (isSlash && !inField)) {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Reset activeIdx når resultater ændrer sig
  useEffect(() => { setActiveIdx(0); }, [query]);

  // Klik udenfor → luk
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleSelect = (item) => {
    if (onSelectResult) onSelectResult(item.ref, item.kind);
    setOpen(false);
    setQuery('');
  };

  const onKeyDownInput = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, Math.max(0, results.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && results[activeIdx]) {
      e.preventDefault();
      handleSelect(results[activeIdx]);
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute left-3 z-[1000]"
      style={{ pointerEvents: 'auto', top: 'calc(0.75rem + 90px)' }}
      data-testid="map-search-bar"
    >
      {!open ? (
        <button
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          className="bg-white hover:bg-zinc-50 shadow-md border border-zinc-200 rounded-full p-2.5 transition-colors"
          title="Søg på kortet (Ctrl+K)"
          data-testid="map-search-open-btn"
          aria-label="Søg på kortet"
        >
          <Search className="h-4 w-4 text-zinc-700" />
        </button>
      ) : (
        <div className="bg-white shadow-xl border border-zinc-200 rounded-lg overflow-hidden" style={{ width: 'min(320px, calc(100vw - 24px))' }}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100">
            <Search className="h-4 w-4 text-zinc-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDownInput}
              placeholder="Søg navn, område, type, fraktion..."
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-400 text-zinc-900"
              data-testid="map-search-input"
              autoFocus
            />
            <button
              onClick={() => { setOpen(false); setQuery(''); }}
              className="text-zinc-400 hover:text-zinc-700 flex-shrink-0"
              aria-label="Luk søgning"
              data-testid="map-search-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {query.trim() && (
            <div className="max-h-80 overflow-y-auto" data-testid="map-search-results">
              {results.length === 0 ? (
                <div className="p-4 text-center text-xs text-zinc-500">
                  Ingen resultater for "{query}"
                </div>
              ) : (
                <>
                  <div className="px-3 py-1.5 text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-100 bg-zinc-50">
                    {results.length} {results.length === 1 ? 'resultat' : 'resultater'}
                  </div>
                  <ul role="listbox">
                    {results.map((item, idx) => {
                      const isActive = idx === activeIdx;
                      const subParts = [item.typeLabel, item.areaName, ...item.fractions].filter(Boolean);
                      return (
                        <li
                          key={`${item.kind}-${item.id}`}
                          role="option"
                          aria-selected={isActive}
                          className={`px-3 py-2 cursor-pointer flex items-start gap-2.5 border-b border-zinc-50 last:border-b-0 ${isActive ? 'bg-emerald-50' : 'hover:bg-zinc-50'}`}
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => handleSelect(item)}
                          data-testid={`map-search-result-${item.id}`}
                        >
                          <div className="mt-1 flex-shrink-0">
                            {item.hasCoords ? (
                              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: statusColor(item.status) }} />
                            ) : (
                              <AlertCircle className="h-3 w-3 text-amber-500" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-zinc-900 truncate">{item.name || <span className="italic text-zinc-400">(uden navn)</span>}</div>
                            <div className="text-[11px] text-zinc-500 truncate">
                              {subParts.join(' · ')}
                            </div>
                            {!item.hasCoords && (
                              <div className="text-[11px] text-amber-600 mt-0.5">Mangler placering på kortet</div>
                            )}
                          </div>
                          <MapPin className="h-3.5 w-3.5 text-zinc-300 mt-1 flex-shrink-0" />
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}
          {!query.trim() && (
            <div className="px-3 py-2 text-[11px] text-zinc-400 leading-relaxed">
              Tip: Skriv et navn (fx "B5"), område ("Greenfield") eller type ("kaffe", "vand")
            </div>
          )}
        </div>
      )}
    </div>
  );
}
